import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import { Receipt } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetQuotes } from '../redux/sagas/requests/quote';
import { statusColor } from '../utils/statusColors';
import { formatDateTime } from '../utils/dateUtils';

const QuoteList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const QuoteRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:last-child': {
        borderBottom: 'none',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
    },
}));

const QuoteName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: theme.spacing(1),
}));

const QuoteStatusChip = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$statuscolor',
})(({ theme, $statuscolor }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    backgroundColor: $statuscolor || '#757575',
    color: '#fff',
    whiteSpace: 'nowrap',
    flexShrink: 0,
}));

const QuoteDate = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    marginLeft: theme.spacing(1),
    flexShrink: 0,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Receipt)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 0, 0.5),
    '&:not(:first-of-type)': {
        marginTop: theme.spacing(1.5),
    },
}));

const SECTION_ORDER = ['Pending', 'Draft', 'Under Review'];

const ManagerQuotesWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data: quotes, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetQuotes({ status: 'Pending,Draft,Under Review', sort: '-updatedAt', limit: 25 }),
        cacheKey: 'manager-quotes-widget',
    });

    const displayQuotes = quotes || [];
    const hasData = displayQuotes.length > 0;

    const grouped = SECTION_ORDER
        .map((status) => ({ status, items: displayQuotes.filter((q) => q.status === status) }))
        .filter((group) => group.items.length > 0);

    return (
        <BaseWidget
            title="Open Quotes"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">No open quotes</Typography>
                </EmptyState>
            ) : (
                <QuoteList>
                    {grouped.map((group) => (
                        <React.Fragment key={group.status}>
                            <SectionHeader>{group.status}</SectionHeader>
                            {group.items.map((quote) => (
                                <QuoteRow key={quote._id} onClick={() => navigate(`/quotes/${quote._id}`)}>
                                    <QuoteName>{quote.name || quote.projectType}</QuoteName>
                                    <QuoteStatusChip $statuscolor={statusColor(theme, 'quote', quote.status)}>
                                        {quote.status}
                                    </QuoteStatusChip>
                                    <QuoteDate>{formatDateTime(quote.createdAt) || 'N/A'}</QuoteDate>
                                </QuoteRow>
                            ))}
                        </React.Fragment>
                    ))}
                </QuoteList>
            )}
        </BaseWidget>
    );
};

export default ManagerQuotesWidget;
